import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TDD_ELIGIBILITY_SEASON } from '@/lib/season';
import { useLeagueConfig } from '@/hooks/useLeagueConfig';

interface TeamStanding {
  id?: number;
  name: string;
  logo?: string;
  position?: number;
  points?: number;
  eligible: boolean;
  promoted?: boolean;
}

interface StandingsData {
  season: string;
  tddIneligibleTeams: TeamStanding[];
  tddEligibleTeams: TeamStanding[];
  competitionId?: number;
  competitionName?: string;
  lastUpdated: string;
}

export const useStandings = (season: string = TDD_ELIGIBILITY_SEASON) => {
  const { competitionId, competition, config: leagueConfig, hasPromotion } = useLeagueConfig();
  const tddIneligibleCount = leagueConfig.tdd_ineligible_count;

  return useQuery({
    queryKey: ['standings', season, competitionId, tddIneligibleCount],
    enabled: competitionId != null,
    queryFn: async () => {
      console.log(`Fetching ${competition.name} standings for ${season}-${parseInt(season) + 1} season`);

      const { data, error } = await supabase.functions.invoke('fetch-standings', {
        body: { season, competitionId, tddIneligibleCount, hasPromotion }
      });

      if (error) {
        console.error(`Error fetching ${competition.name} standings:`, error);
        throw error;
      }

      return data as StandingsData;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 3,
    retryDelay: 1000,
  });
};

export const useTDDEligibility = (season: string = TDD_ELIGIBILITY_SEASON) => {
  const { data: standings, isLoading, error } = useStandings(season);

  const getTDDEligibleTeams = () => {
    if (!standings) return [];
    return standings.tddEligibleTeams;
  };

  const getTDDIneligibleTeams = () => {
    if (!standings) return [];
    return standings.tddIneligibleTeams;
  };

  const isTDDEligible = (teamName: string) => {
    if (!standings) return false;
    return standings.tddEligibleTeams.some(team => team.name === teamName);
  };

  return {
    standings,
    isLoading,
    error,
    getTDDEligibleTeams,
    getTDDIneligibleTeams,
    isTDDEligible
  };
};
